import React, { useState, useEffect } from 'react';
import { fetchSiparisDetay, onaylaSiparis } from '../services/siparisService';
import { toValidObjectIdString } from '../utils/security';
import SiparisParcaPicker from './SiparisParcaPicker';
import IntegerInput from './IntegerInput';
import ParcaPickerNestedModal from './ParcaPickerNestedModal';
import { parseAdet } from '../utils/integerInput';
import SiparisNotuCard from './SiparisNotuCard';
import './SiparisOnaylaModal.css';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const SiparisOnaylaModal = ({ siparis: listSiparis, onClose, onSuccess, getSiparisId }) => {
  const [siparis, setSiparis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pozisyonlar, setPozisyonlar] = useState([]);
  const [faturaBilgisi, setFaturaBilgisi] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [parcaPickerOpen, setParcaPickerOpen] = useState(false);
  const [eklenenListeOpen, setEklenenListeOpen] = useState(false);

  const getId = getSiparisId || ((s) => toValidObjectIdString(s?.id ?? s?._id) || s?.siparisNumarasi);

  useEffect(() => {
    const load = async () => {
      const id = getId(listSiparis);
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await fetchSiparisDetay(id);
        if (res?.basarili && res?.veri) {
          setSiparis(res.veri);
          const pos = res.veri.pozisyonlar || [];
          setPozisyonlar(pos.map((p) => ({ ...p, adet: p.adet || 1 })));
        }
      } catch {
        setError('Sipariş detayı yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [listSiparis]);

  const gorunenBirimFiyat = (p) => {
    if (siparis?.garantiDahilinde || siparis?.konsinye) return null;
    if (p.fiyatGarantiSnapshot != null && !Number.isNaN(parseFloat(p.fiyatGarantiSnapshot))) {
      return parseFloat(p.fiyatGarantiSnapshot);
    }
    return p.fiyat != null ? parseFloat(p.fiyat) : null;
  };

  const garantiBirimFromIc = (ic) => {
    if (ic.fiyatgaranti != null && ic.fiyatgaranti !== '') return parseFloat(ic.fiyatgaranti);
    if (ic.fiyatGaranti != null && ic.fiyatGaranti !== '') return parseFloat(ic.fiyatGaranti);
    return null;
  };

  const handleParcaEkle = (ic, adet = 1) => {
    const icerikId = toValidObjectIdString(ic.id || ic._id);
    if (!icerikId) return;

    const qty = Math.max(1, parseInt(adet, 10) || 1);
    const existingIdx = pozisyonlar.findIndex(
      (x) => toValidObjectIdString(x.icerikId || x._id) === icerikId
    );

    if (existingIdx >= 0) {
      setPozisyonlar((prev) =>
        prev.map((p, i) =>
          i === existingIdx ? { ...p, adet: (p.adet || 1) + qty } : p
        )
      );
    } else {
      const fiyatGarantiSnapshot = showPrice ? garantiBirimFromIc(ic) : null;
      setPozisyonlar((prev) => [
        ...prev,
        {
          icerikId,
          parcaAdi: ic.icerikAdi || ic.parcaAdi || ic.urunAdi || ic.ad || '—',
          parcaKodu: ic.icerikKodu || ic.parcaKodu || ic.urunKodu || ic.kod || '—',
          listeFiyati: null,
          fiyatGarantiSnapshot,
          fiyat: null,
          adet: qty,
        },
      ]);
    }
    setError('');
  };

  const openParcaPicker = () => {
    setEklenenListeOpen(false);
    setParcaPickerOpen(true);
  };

  const closeParcaPicker = () => {
    setEklenenListeOpen(false);
    setParcaPickerOpen(false);
  };

  const handleAdetChange = (index, value) => {
    const num = parseAdet(value, { min: 1 });
    setPozisyonlar((prev) =>
      prev.map((p, i) => (i === index ? { ...p, adet: num } : p))
    );
  };

  const handleRemove = (index) => {
    setPozisyonlar((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!siparis) return;
    if (pozisyonlar.length === 0) {
      setError('En az bir pozisyon bulunmalıdır');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const id = getId(listSiparis);
      if (!id) {
        setError('Sipariş ID alınamadı');
        setSubmitting(false);
        return;
      }
      const onayPozisyonlari = pozisyonlar.map((p) => {
        const icerikId = toValidObjectIdString(p.icerikId || p._id);
        if (!icerikId) throw new Error(`Geçersiz parça ID: ${p.parcaAdi}`);
        return { icerikId, adet: p.adet || 1 };
      });
      const res = await onaylaSiparis(id, {
        onayPozisyonlari,
        faturaBilgisi: faturaBilgisi?.trim() || undefined,
      });
      if (res?.basarili) {
        onSuccess?.();
        onClose?.();
      } else {
        setError(res?.mesaj || 'Onay başarısız');
      }
    } catch (err) {
      setError(err?.message || err?.data?.mesaj || 'Onay başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const showPrice = !siparis?.garantiDahilinde && !siparis?.konsinye;

  const renderEklenenParcalarModal = () => {
    if (!eklenenListeOpen) return null;

    return (
      <div
        className="siparis-onayla-eklenen-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Eklenen parçalar"
        onClick={() => setEklenenListeOpen(false)}
      >
        <div className="siparis-onayla-eklenen-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="siparis-onayla-eklenen-head">
            <h3>Eklenen parçalar ({pozisyonlar.length})</h3>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setEklenenListeOpen(false)}
              aria-label="Kapat"
            >
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="siparis-onayla-eklenen-body">
            {pozisyonlar.length === 0 ? (
              <p className="siparis-onayla-eklenen-empty">Henüz parça eklenmedi.</p>
            ) : (
              <div className="siparis-onayla-eklenen-table-wrap">
                <table className="admins-table">
                  <thead>
                    <tr>
                      <th>Parça</th>
                      <th>Kod</th>
                      {!showPrice ? null : <th style={{ textAlign: 'right' }}>Net fiyat (birim)</th>}
                      <th style={{ textAlign: 'center' }}>Adet</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {pozisyonlar.map((p, idx) => (
                      <tr key={`eklenen-${p.icerikId || idx}`}>
                        <td>{p.parcaAdi || '—'}</td>
                        <td>{p.parcaKodu || '—'}</td>
                        {!showPrice ? null : (
                          <td style={{ textAlign: 'right' }}>{formatCurrency(gorunenBirimFiyat(p))}</td>
                        )}
                        <td style={{ textAlign: 'center' }}>
                          <IntegerInput
                            min={1}
                            value={p.adet || 1}
                            onChange={(v) => handleAdetChange(idx, v)}
                            className="parca-sec-qty-input"
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="category-action-btn delete-btn"
                            onClick={() => handleRemove(idx)}
                            title="Kaldır"
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="siparis-onayla-eklenen-footer">
            <button type="button" className="btn btn-primary" onClick={() => setEklenenListeOpen(false)}>
              Listeye dön
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!listSiparis) return null;

  return (
    <div className="modal-overlay siparis-onayla-modal" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Sipariş Onayla - {listSiparis.siparisNumarasi}</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="siparis-onayla-loading">Yükleniyor...</div>
          ) : (
            <>
              <SiparisNotuCard not={siparis?.not} className="siparis-notu-card--onayla" />
              <p className="siparis-onayla-desc">
                Parça eklemek için kategori ağacından seçim yapın. Aşağıdaki tabloda yalnızca onaya
                dahil edilen parçalar listelenir; adet düzenleyebilir veya satır silebilirsiniz.
                {showPrice
                  ? ' Garanti dışı siparişlerde net fiyat (birim) gösterilir; kesin tutar onayda işlenir.'
                  : ''}
              </p>
              <div className="parca-sec-open-picker-row">
                <button type="button" className="btn btn-secondary parca-sec-open-picker" onClick={openParcaPicker}>
                  <i className="fas fa-layer-group" aria-hidden /> Parça Seç
                </button>
              </div>
              <div className="parca-sec-table-wrap">
                {pozisyonlar.length === 0 ? (
                  <div className="parca-sec-empty-positions">
                    Henüz parça eklenmedi. Yukarıdaki düğmeyle parça seçin.
                  </div>
                ) : (
                  <table className="admins-table">
                    <thead>
                      <tr>
                        <th>Parça</th>
                        <th>Kod</th>
                        {showPrice ? <th style={{ textAlign: 'right' }}>Net fiyat (birim)</th> : null}
                        <th style={{ textAlign: 'center' }}>Adet</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pozisyonlar.map((p, idx) => (
                        <tr key={`${p.icerikId || idx}`}>
                          <td>{p.parcaAdi || '—'}</td>
                          <td>{p.parcaKodu || '—'}</td>
                          {showPrice ? (
                            <td style={{ textAlign: 'right' }}>{formatCurrency(gorunenBirimFiyat(p))}</td>
                          ) : null}
                          <td style={{ textAlign: 'center' }}>
                            <IntegerInput
                              min={1}
                              value={p.adet || 1}
                              onChange={(v) => handleAdetChange(idx, v)}
                              className="parca-sec-qty-input"
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="category-action-btn delete-btn"
                              onClick={() => handleRemove(idx)}
                              title="Kaldır"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {!siparis?.garantiDahilinde && !siparis?.konsinye && (
                <div className="siparis-onayla-form-group">
                  <label>Fatura Bilgisi (opsiyonel - sonra Fatura Bekleyen sekmesinden girilebilir)</label>
                  <input
                    type="text"
                    value={faturaBilgisi}
                    onChange={(e) => setFaturaBilgisi(e.target.value)}
                    placeholder="Fatura no veya bilgi"
                    className="siparis-onayla-input"
                  />
                </div>
              )}
              {siparis?.konsinye && (
                <p className="siparis-onayla-info">
                  Konsinye siparişlerde fiyat oluşturulmaz. Sevk irsaliyesi kodu, kargo sonrası Fatura Bekleyen sekmesinden girilir.
                </p>
              )}
              {siparis?.garantiDahilinde && (
                <p className="siparis-onayla-info">Garanti dahilinde sipariş onaylandığında iade kargo talebi otomatik oluşturulacaktır.</p>
              )}
              {error && <div className="siparis-onayla-error">{error}</div>}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            İptal
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || submitting || pozisyonlar.length === 0}
          >
            {submitting ? 'Onaylanıyor...' : 'Onayla'}
          </button>
        </div>
      </div>

      <ParcaPickerNestedModal
        open={parcaPickerOpen}
        onClose={closeParcaPicker}
        title="Parça seç — kategori ve ürün"
        selectedCount={pozisyonlar.length}
        onShowSelected={() => setEklenenListeOpen(true)}
        overlayExtras={renderEklenenParcalarModal()}
      >
        <SiparisParcaPicker
          showPrice={showPrice}
          onAddPart={handleParcaEkle}
          birimFiyatFromParca={showPrice ? garantiBirimFromIc : undefined}
        />
      </ParcaPickerNestedModal>
    </div>
  );
};

export default SiparisOnaylaModal;